// src/main/shell/nav-tree.ts
//
// The whole app modeled as ONE tree rooted at the app entry. Every page is a
// node with a depth + parent, so any navigation carries a spatial DIRECTION
// derived purely from the tree relationship between source and target:
//
//   • toward a CHILD (deeper)   → slide horizontally RIGHT (push in from the right)
//   • toward a PARENT/ANCESTOR  → slide horizontally LEFT  (pop back to the left)
//   • to a SIBLING (same depth) → slide VERTICALLY, ordered like a list
//
// This module is deliberately GENERIC (it knows nothing about gateways or
// controllers) so it extends to ANY page→ANY page as the route table grows —
// the caller just maps its route to a NavNode. Today's tree is
// home(0) → gateway(1) → controller(2), but symbol/program/model sub-pages slot
// in at depth 3+ with zero changes here.
//
// PHASE 1 (this file): the NET single-hop direction — slideDirection(). It
// delivers the spatial feel for every adjacent move and a sensible
// approximation for multi-level jumps (one longer slide in the net direction).
//
// PHASE 2 (the "shoot through every intermediate node" variant): navPath() walks
// the tree via the lowest common ancestor (up to the LCA, then down to the
// target) and returns each edge as an ordered hop. A future animator plays those
// hops in sequence with faked intermediate frames. The path-finder lives here so
// the tree model is the single source of truth for both phases.
/**
 * The single-hop NET direction between two pages. `from` undefined (first paint)
 * or an identical key → 'none' (no animation).
 */
export function slideDirection(from, to) {
    if (!from || from.key === to.key)
        return 'none';
    if (to.depth > from.depth)
        return 'right'; // descending toward a child
    if (to.depth < from.depth)
        return 'left'; // ascending toward a parent/ancestor
    // Same depth, different branch → siblings. Order them like a vertical list: a
    // later sibling enters from below (content moves up), an earlier one from above.
    return to.order >= from.order ? 'up' : 'down';
}
/**
 * PHASE 2 — the full tree PATH from `from` to `to`: up to the lowest common
 * ancestor, then down to the target, each element one tree node along the way
 * (excluding `from`, including `to`). `lookup` resolves a key to its node so the
 * walk can climb parent chains. Returns the ordered hop list the multi-hop
 * animator will play (with faked intermediate frames). Pure tree math; no DOM.
 *
 * Not yet consumed by the animator (v1 uses slideDirection); kept so the model is
 * authoritative for both phases and v2 is a localized addition, not a rewrite.
 */
export function navPath(from, to, lookup) {
    if (from.key === to.key)
        return [];
    // Ancestor chain of `to` (target → ... → root), with depths, for LCA lookup.
    const toChain = [];
    for (let n = to; n; n = n.parentKey ? lookup(n.parentKey) : undefined)
        toChain.push(n);
    const toKeys = new Set(toChain.map((n) => n.key));
    // Climb from `from` until we hit an ancestor shared with `to` (the LCA).
    const up = [];
    let cursor = from;
    while (cursor && !toKeys.has(cursor.key)) {
        const parent = cursor.parentKey ? lookup(cursor.parentKey) : undefined;
        if (parent)
            up.push(parent); // each parent we ascend through is a hop
        cursor = parent;
    }
    const lca = cursor;
    if (!lca)
        return [to]; // disjoint trees (shouldn't happen) → fall back to a direct hop
    // Descend from the LCA down to the target (reverse of toChain above the LCA).
    const down = [];
    for (const n of toChain) {
        if (n.key === lca.key)
            break;
        down.push(n);
    }
    down.reverse();
    return [...up, ...down];
}
//# sourceMappingURL=nav-tree.js.map